'use client';

import { useEffect } from 'react';

/**
 * Hook to prefetch drug interactions in the background.
 * Fires immediately when 2+ drugs are selected and IDs change.
 *
 * @param enabled – pass `false` to suppress the fetch (e.g. when drugs were
 *   loaded from the URL and no user interaction has happened yet).
 */
export function useDrugInteractionsPrefetch(drugIds: string[], enabled = true) {
  const drugIdsKey = drugIds.length >= 2 ? [...drugIds].sort().join(',') : '';

  useEffect(() => {
    if (!drugIdsKey || !enabled) return;

    fetch('/api/interactions/prefetch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ drugIds: drugIdsKey.split(',') }),
    })
      .then((response) => {
        // The first use of a pilot starts its clock; tell the header so it
        // shows the end date without a page reload.
        const endsAt = response.headers.get('x-pilot-ends-at');

        if (endsAt) {
          window.dispatchEvent(
            new CustomEvent('pilotClockStarted', { detail: { endsAt } }),
          );
        }
      })
      .catch(() => {
        // Silently fail - prefetch is best effort
      });
  }, [drugIdsKey, enabled]);
}
